// Resumen "Material a alquilar" (columna derecha de la pestaña Material).
//
// Enseña de un vistazo en qué punto está el alquiler del evento: chip de
// estado, proveedor, artículos, coste, cobro al cliente y margen, entrega y
// recogida de la orden, el siguiente paso y UN botón ("Añadir alquiler" sin
// líneas, "Abrir alquiler" con líneas) que despliega el panel completo
// (AlquilerEvento) encima del material. Debajo, en gris, lo que llega al
// evento y el estado de la recepción.
//
// `datos` es la respuesta del GET /presupuestos/{id}/alquiler; no pide nada.
import React from 'react';

const botonPrincipal = 'h-[34px] px-3 rounded-lg border text-[13px] font-bold whitespace-nowrap bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90';

const euros = (n) => `${(Number(n) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const formatFecha = (valor) => {
  const t = String(valor || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
  return `${t.slice(8, 10)}/${t.slice(5, 7)}`;
};

const formatFechaHora = (valor) => {
  if (!valor) return '';
  const texto = String(valor);
  const f = new Date(/Z|[+-]\d{2}:?\d{2}$/.test(texto) ? texto : `${texto}Z`);
  if (Number.isNaN(f.getTime())) return '';
  return f.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const CHIPS = {
  sin_alquiler: { texto: 'Sin alquiler', clase: 'bg-gray-100 text-gray-600' },
  sin_pedir: { texto: 'Sin pedir', clase: 'bg-amber-100 text-amber-700' },
  enviado: { texto: 'Pedido enviado', clase: 'bg-green-100 text-green-700' },
  confirmado: { texto: 'Confirmado', clase: 'bg-blue-100 text-blue-700' },
  hoy: { texto: 'Hoy llega', clase: 'bg-purple-100 text-purple-700' },
  celebrado: { texto: 'Celebrado', clase: 'bg-gray-200 text-gray-700' },
};

// Lo pedido cambió después de enviar la orden (cantidades o artículos)
const cambiadoTrasEnvio = (lineas, orden) => {
  if (!orden || !['enviada', 'confirmada'].includes(orden.estado)) return false;
  const firma = (lista) => (lista || [])
    .filter(l => Number(l.cantidad) > 0)
    .map(l => `${l.material_id}:${Number(l.cantidad)}`)
    .sort()
    .join('|');
  return firma(lineas) !== firma(orden.lineas);
};

export const estadoResumenAlquiler = (datos, fechaEvento) => {
  const lineas = Array.isArray(datos?.lineas) ? datos.lineas : [];
  const menus = Array.isArray(datos?.menus_alquiler) ? datos.menus_alquiler : [];
  if (lineas.length === 0 && menus.length === 0) return 'sin_alquiler';
  const estadoOrden = datos?.orden?.estado || null;
  if (datos?.evento_celebrado) return 'celebrado';
  const hoy = hoyISO();
  const inicio = String(fechaEvento || '').slice(0, 10);
  const fin = String(datos?.fecha_fin_evento || inicio).slice(0, 10);
  const enviadaOConfirmada = ['enviada', 'confirmada'].includes(estadoOrden);
  if (inicio && inicio <= hoy && hoy <= (fin || inicio) && (enviadaOConfirmada || lineas.length === 0)) return 'hoy';
  if (['confirmada', 'recogida', 'devuelta'].includes(estadoOrden)) return 'confirmado';
  if (estadoOrden === 'enviada') return 'enviado';
  // Solo menús "Es alquiler" (sin alquiler del catálogo): nada que pedir aquí
  return lineas.length > 0 ? 'sin_pedir' : 'sin_alquiler';
};

const ResumenAlquiler = ({ datos, fechaEvento = null, onAbrir }) => {
  const lineas = Array.isArray(datos?.lineas) ? datos.lineas : [];
  const menus = Array.isArray(datos?.menus_alquiler) ? datos.menus_alquiler : [];
  const orden = datos?.orden || null;
  const estadoOrden = orden?.estado || null;
  const totales = datos?.totales || {};
  const recepcion = datos?.recepcion || null;
  const proveedor = orden?.proveedor_nombre || datos?.proveedor?.nombre || 'el proveedor';
  const estado = estadoResumenAlquiler(datos, fechaEvento);
  const chip = CHIPS[estado];
  const numArticulos = lineas.length + menus.length;
  const conLineas = lineas.length > 0;
  const incidencias = String(recepcion?.incidencias || '').trim();

  let siguiente;
  if (estado === 'sin_alquiler') {
    siguiente = menus.length > 0
      ? 'Lleva menús de alquiler: lo trae el proveedor y se comprueba en la hoja de material.'
      : 'Este evento no lleva material de alquiler.';
  }
  else if (estado === 'sin_pedir') {
    siguiente = datos?.validado && !datos?.huella_cambiada
      ? `Prepara y envía la orden a ${proveedor}.`
      : 'Valida artículos y cantidades.';
  } else if (estado === 'enviado') {
    siguiente = orden?.cotizacion_recibida?.estado === 'pendiente'
      ? 'Cotización recibida: revísala.'
      : `Esperando la cotización de ${proveedor}.`;
  } else if (estado === 'confirmado') siguiente = 'Coste real aplicado. Nada pendiente hasta el evento.';
  else if (estado === 'hoy') {
    if (incidencias) siguiente = `Recepción con incidencias: ${incidencias}`;
    else if (recepcion?.comprobada) siguiente = `Recepción comprobada${recepcion.por ? ` por ${recepcion.por}` : ''}${recepcion.en ? ` el ${formatFechaHora(recepcion.en)}` : ''}.`;
    else siguiente = 'Confirma lo que ha llegado con la hoja de material.';
  } else if (estado === 'celebrado') {
    if (!conLineas || estadoOrden === 'devuelta') siguiente = 'Nada pendiente.';
    else siguiente = 'Marca la recogida y la devolución de la fianza.';
  }

  const margen = totales.margen_total_pct;
  const costeVisible = totales.coste_real ?? totales.coste_referencia ?? totales.coste_total;

  return (
    <div className="flex flex-col gap-3" data-testid="alquiler-resumen" data-estado={estado}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs font-bold tracking-wider uppercase text-[#5B6B64]">Material a alquilar</h3>
        <span className="flex flex-wrap gap-1">
          <span className={`px-2.5 py-0.5 rounded-md text-xs font-semibold ${chip.clase}`} data-testid="alquiler-resumen-estado">{chip.texto}</span>
          {estado === 'celebrado' && ['recogida', 'devuelta'].includes(estadoOrden) && (
            <span className="px-2.5 py-0.5 rounded-md text-xs font-semibold bg-gray-100 text-gray-700">
              {estadoOrden === 'recogida' ? 'Recogida' : 'Devuelta'}
            </span>
          )}
        </span>
      </div>

      {datos?.huella_cambiada && (
        <p className="text-xs font-semibold rounded-lg bg-[#FDF0DC] text-[#7A3A06] px-2.5 py-2" data-testid="alquiler-resumen-huella">
          Ha cambiado desde la validación
        </p>
      )}
      {cambiadoTrasEnvio(lineas, orden) && (
        <p className="text-xs font-semibold rounded-lg bg-[#FDF0DC] text-[#7A3A06] px-2.5 py-2" data-testid="alquiler-resumen-reenviar">
          Has cambiado el alquiler después de enviar el pedido. Reenvíalo a {proveedor === 'el proveedor' ? 'Alea' : proveedor}.
        </p>
      )}

      {numArticulos > 0 && (
        <p className="text-sm text-[#16221D]">
          {conLineas ? <span className="font-semibold">{proveedor === 'el proveedor' ? 'Proveedor' : proveedor}</span> : null}
          {conLineas ? ' · ' : ''}{numArticulos} {numArticulos === 1 ? 'artículo' : 'artículos'}
        </p>
      )}

      {conLineas && (
        <div className="text-sm flex flex-col gap-0.5 border-t border-[#EEF1EF] pt-2" data-testid="alquiler-resumen-importes">
          <div className="flex justify-between"><span className="text-[#5B6B64]">Coste</span><span className="tabular-nums">{euros(costeVisible)}</span></div>
          <div className="flex justify-between"><span className="text-[#5B6B64]">Cobro al cliente</span><span className="tabular-nums">{euros(totales.venta_total)}</span></div>
          <div className="flex justify-between">
            <span className="text-[#5B6B64]">Margen</span>
            <span className="tabular-nums">{margen === null || margen === undefined ? '-' : `${margen > 0 ? '+' : ''}${Number(margen).toLocaleString('es-ES', { maximumFractionDigits: 1 })} %`}</span>
          </div>
        </div>
      )}

      {orden && (orden.fecha_entrega || orden.fecha_recogida) && (
        <div className="text-xs text-[#16221D] flex flex-col gap-0.5 border-t border-[#EEF1EF] pt-2">
          {orden.fecha_entrega && <span>Entrega {formatFecha(orden.fecha_entrega)}{orden.hora_entrega ? ` ${orden.hora_entrega}` : ''}</span>}
          {orden.fecha_recogida && <span>Recogida {formatFecha(orden.fecha_recogida)}{orden.hora_recogida ? ` ${orden.hora_recogida}` : ''}</span>}
        </div>
      )}

      <div className="border-t border-[#EEF1EF] pt-2 flex flex-col gap-2">
        <p className="text-xs text-[#16221D]" data-testid="alquiler-resumen-siguiente">
          <span className="font-semibold">Siguiente paso: </span>{siguiente}
        </p>
        <button type="button" onClick={onAbrir} className={`${botonPrincipal} self-start`} data-testid="alquiler-resumen-abrir">
          {conLineas ? 'Abrir alquiler' : 'Añadir alquiler'}
        </button>
      </div>

      {datos?.hay_recepcion && (
        <p className="text-xs text-[#8A958F]" data-testid="alquiler-resumen-recepcion">
          Llega al evento (no se carga). Recepción:{' '}
          {recepcion?.comprobada ? 'comprobada' : 'pendiente'}
          {incidencias ? ' · con incidencias' : ''}
        </p>
      )}
    </div>
  );
};

export default ResumenAlquiler;
